import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '@/shared/hooks';
import { apiClient } from '@/shared/api/client';
import { endpoints } from '@/shared/api/endpoints';
import { getEndpoint, getMutationMethod } from '@/shared/utils/getUrl';
import type { EmployeesListResponse, EmployeesListResult, EmployeesParams, EmployeeCreateRequest, EmployeeCreateResponse } from './types';
import { toast } from '@/shared/store/useToastStore';

/**
 * Хук для получения списка сотрудников
 */
export const useEmployeesListQuery = (params?: EmployeesParams) => {
  const url = getEndpoint(endpoints.employees.list, params);
  return useQuery<EmployeesListResult>({
    queryKey: ['employees', 'list', params ? JSON.stringify(params) : 'all'],
    queryFn: async ({ signal }) => {
      const response = await apiClient.get<EmployeesListResponse>(url, { signal });
      const { users, total_count: totalCount } = response.data;
      return { items: users, totalCount };
    },
    enabled: true,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: 30000,
  });
};

/**
 * Хук для создания сотрудника
 */
export const useEmployeesCreateMutation = () => {
  return useApiMutation<EmployeeCreateResponse, EmployeeCreateRequest>(
    getEndpoint(endpoints.employees.create),
    getMutationMethod(endpoints.employees.create),
    {
      invalidateQueries: [['employees', 'list']],
      onSuccess: () => {
        toast.success('Сотрудник успешно добавлен');
      },
      onError: (error) => {
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (error as { message?: string })?.message ||
          'Ошибка при добавлении сотрудника';
        toast.error(errorMessage);
      },
    }
  );
};
